<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import { UiButton, UiText } from '@shopexperts/shared';
import RegisterForm, { type RegisterAuthResult } from './leads/RegisterForm.vue';

const emit = defineEmits<{
    (e: 'authenticated', data: RegisterAuthResult): void;
}>();

const router = useRouter();

const registerFormRef = useTemplateRef<InstanceType<typeof RegisterForm>>('registerFormRef');

const isLoading = computed(() => registerFormRef.value?.isLoading ?? false);

const websiteUrl = (import.meta.env.VITE_WEBSITE_URL as string | undefined)?.replace(/\/$/, '') || '';
const termsClientHref = `${websiteUrl}/policies/terms-client`;
const privacyHref = `${websiteUrl}/policies/privacy`;

function handleSignUp() {
    registerFormRef.value?.triggerSubmit();
}

function handleAuthenticated(data: RegisterAuthResult) {
    emit('authenticated', data);
}

function goToLogin() {
    router.push('/login');
}
</script>

<template>
    <div class="flex flex-col gap-8">
        <!-- Header: Already have an account? Login -->
        <div class="flex flex-col gap-4">
            <UiText as="p" variant="p">
                <span>Already have an account? </span>
                <RouterLink to="/login" class="hover:underline text-se-highlight">
                    <UiText as="span" variant="p">
                        <span>Login</span>
                    </UiText>
                </RouterLink>
            </UiText>

            <!-- Title -->
             <UiText as="h1" variant="h3" text="Open a Free **Client Account**" />

            <!-- Subtitle -->
            <UiText as="p" variant="p" class="text-se-gray-600">
                <span>Dive into our Shopify experts directory and connect with top global and local experts in just minutes – no fuss, just results!</span>
            </UiText>
        </div>

        <!-- Register Form -->
        <RegisterForm
            ref="registerFormRef"
            @authenticated="handleAuthenticated"
            @switch-to-login="goToLogin"
        />

        <!-- Sign up button and terms -->
        <div class="flex flex-col gap-4">
            <UiButton
                type="button"
                variant="primary"
                label="Sign up"
                class="w-full"
                :disabled="isLoading"
                @click="handleSignUp"
            />

            <UiText as="p" variant="caption-normal" class="text-center text-se-gray-600">
                <span>By proceeding, you agree to the </span>
                <a :href="termsClientHref" class="hover:underline text-se-highlight" target="_blank" rel="noopener noreferrer">
                    <UiText as="span" variant="caption-normal">Terms &amp; Conditions</UiText>
                </a>
                <span> and </span>
                <a :href="privacyHref" class="hover:underline text-se-highlight" target="_blank" rel="noopener noreferrer">
                    <UiText as="span" variant="caption-normal">Privacy Policy</UiText>
                </a>
                <span>.</span>
            </UiText>
        </div>
    </div>
</template>

